import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { isNone } from "~/common/assert";
import { isAdditionalAlignersCourse, isChildrenShortCourse } from "~/common/courses";
import { getLastCorrection } from "~/common/patient";
import { FormatDate } from "~/components/common/FormatDate";
import { PersonName } from "~/components/common/PersonName";
import { LabelledValue } from "~/components/ui/labelled-value";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import type { TPatient } from "~/types/patient";

export function PatientId({ patient }: { patient: TPatient }) {
  const intl = useIntl();

  const { warranty_end_date, bonus } = patient;
  const { start_date, steps_count_total, approval_timestamp } = getLastCorrection(patient);

  return (
    <Portlet id="patient-info-side-block">
      <PortletTitle as="h2" iconClassName="icon-user">
        <FormattedMessage id="pat.block.right.header" />

        {bonus ? (
          <img
            style={{ width: "2rem", marginLeft: "1rem" }}
            src="/img/bonus.svg"
            title={intl.formatMessage({ id: "bonus.patient" })}
            alt=""
          />
        ) : null}
      </PortletTitle>

      <div className="portlet-body" id="patient-info-side-block-body">
        <LabelledValue label={<FormattedMessage id="pat.block.right.id" />}>
          {patient.patient_id}
        </LabelledValue>

        {isAdditionalAlignersCourse(patient.course.course_id) ? (
          <LabelledValue label={<FormattedMessage id="pat.block.right.name" />}>
            <Link to={`/pages/patient/${patient.original_patient?.patient_id}`}>
              <PersonName person={patient} />
            </Link>
          </LabelledValue>
        ) : (
          <LabelledValue label={<FormattedMessage id="pat.block.right.name" />}>
            <PersonName person={patient} />
          </LabelledValue>
        )}

        <LabelledValue label={<FormattedMessage id="pat.block.right.instructions" />}>
          <FormatDate value={patient.timestamp} date />
        </LabelledValue>

        {warranty_end_date ? (
          <LabelledValue label={<FormattedMessage id="pat.block.right.warranty_end_date" />}>
            <FormatDate value={warranty_end_date} date />
          </LabelledValue>
        ) : null}

        {patient.course.correction.slice(1).map((correction, index) => (
          <LabelledValue
            key={index}
            label={
              <>
                <FormattedMessage
                  id={
                    isChildrenShortCourse(patient.course.course_id)
                      ? "pat.block.right.additional_course"
                      : "pat.block.right.correction"
                  }
                />
                {` ${index + 1}`}
              </>
            }
          >
            <FormatDate value={correction.timestamp} date />
          </LabelledValue>
        ))}

        {start_date ? (
          <LabelledValue label={<FormattedMessage id="pat.block.right.start_date" />}>
            <FormatDate value={start_date} date />
          </LabelledValue>
        ) : null}

        {approval_timestamp ? (
          <LabelledValue label={<FormattedMessage id="pat.block.right.plan_approval" />}>
            <FormatDate value={approval_timestamp} date />
          </LabelledValue>
        ) : null}

        {!isNone(steps_count_total) ? (
          <LabelledValue label={<FormattedMessage id="p.patient.correction.stages" />}>
            {steps_count_total}
          </LabelledValue>
        ) : null}
      </div>
    </Portlet>
  );
}
